import React, { useState, useEffect } from 'react';
import { Wind, TrendingUp, Minus, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { getVesselLocation } from '../config/locations';

const WeatherPanel = () => {
  const vesselLocation = getVesselLocation();
  const [weatherData, setWeatherData] = useState({
    wind: { value: '-- kt', numValue: 0, trend: 'steady', history: [0, 0, 0, 0, 0, 0, 0], safeMax: 25, cautionMax: 30 },
    waves: { value: '-- m', numValue: 0, trend: 'steady', history: [0, 0, 0, 0, 0, 0, 0], safeMax: 3.0, cautionMax: 4.0 },
    surfaceTemp: { value: '-- °C', numValue: 0, trend: 'steady', history: [0, 0, 0, 0, 0, 0, 0], optimalMin: 18, optimalMax: 26 },
    pressure: { value: '-- hPa', numValue: 0, trend: 'steady', history: [0, 0, 0, 0, 0, 0, 0], lowThreshold: 1000, highThreshold: 1020 },
  });
  const [dataSource, setDataSource] = useState('Loading...');
  const [isRealData, setIsRealData] = useState(false);

  // Fetch real weather data from backend
  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/weather/buoy?lat=${vesselLocation.lat}&lon=${vesselLocation.lon}`);
        const result = await response.json();
        
        if (result.success && result.data) {
          const data = result.data;
          setIsRealData(true);
          setDataSource(result.source);
          
          // Update with real data, use reasonable defaults for null values
          setWeatherData(prev => ({
            wind: {
              ...prev.wind,
              value: data.windSpeed ? `${data.windSpeed.toFixed(1)} kt` : '11.0 kt',
              numValue: data.windSpeed || 11.0,
              history: [...prev.wind.history.slice(1), data.windSpeed || 11.0]
            },
            waves: {
              ...prev.waves,
              value: data.waveHeight ? `${data.waveHeight.toFixed(1)} m` : '2.1 m',
              numValue: data.waveHeight || 2.1,
              history: [...prev.waves.history.slice(1), data.waveHeight || 2.1]
            },
            surfaceTemp: {
              ...prev.surfaceTemp,
              value: data.waterTemperature ? `${data.waterTemperature.toFixed(1)} °C` : '26.2 °C',
              numValue: data.waterTemperature || 26.2,
              history: [...prev.surfaceTemp.history.slice(1), data.waterTemperature || 26.2]
            },
            pressure: {
              ...prev.pressure,
              value: data.atmosphericPressure ? `${data.atmosphericPressure.toFixed(0)} hPa` : '1021 hPa',
              numValue: data.atmosphericPressure || 1021,
              history: [...prev.pressure.history.slice(1), data.atmosphericPressure || 1021]
            }
          }));
        } else {
          setDataSource('Simulated Data');
          // Use simulated data as fallback
          setWeatherData({
            wind: { value: '17 kt WSW', numValue: 17, trend: 'steady', history: [15, 16, 17, 17, 18, 17, 17], safeMax: 25, cautionMax: 30 },
            waves: { value: '2.1 m', numValue: 2.1, trend: 'rising', history: [1.8, 2.0, 1.9, 2.1, 2.0, 2.1, 2.2], safeMax: 3.0, cautionMax: 4.0 },
            surfaceTemp: { value: '21.4 °C', numValue: 21.4, trend: 'steady', history: [21.2, 21.3, 21.4, 21.4, 21.3, 21.4, 21.4], optimalMin: 18, optimalMax: 26 },
            pressure: { value: '1012 hPa', numValue: 1012, trend: 'falling', history: [1016, 1015, 1014, 1013, 1013, 1012, 1012], lowThreshold: 1000, highThreshold: 1020 },
          });
        }
      } catch (error) {
        console.error('Error fetching weather data:', error);
        setDataSource('Simulated Data (API Error)');
        setIsRealData(false);
        // Set simulated fallback data
        setWeatherData({
          wind: { value: '17 kt WSW', numValue: 17, trend: 'steady', history: [15, 16, 17, 17, 18, 17, 17], safeMax: 25, cautionMax: 30 },
          waves: { value: '2.1 m', numValue: 2.1, trend: 'rising', history: [1.8, 2.0, 1.9, 2.1, 2.0, 2.1, 2.2], safeMax: 3.0, cautionMax: 4.0 },
          surfaceTemp: { value: '21.4 °C', numValue: 21.4, trend: 'steady', history: [21.2, 21.3, 21.4, 21.4, 21.3, 21.4, 21.4], optimalMin: 18, optimalMax: 26 },
          pressure: { value: '1012 hPa', numValue: 1012, trend: 'falling', history: [1016, 1015, 1014, 1013, 1013, 1012, 1012], lowThreshold: 1000, highThreshold: 1020 },
        });
      }
    };

    fetchWeatherData();
    // Refresh every 5 minutes
    const interval = setInterval(fetchWeatherData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setWeatherData(prev => ({
        ...prev,
        waves: {
          ...prev.waves,
          value: `${(2.1 + (Math.random() - 0.5) * 0.2).toFixed(1)} m`,
          history: [...prev.waves.history.slice(1), 2.1 + (Math.random() - 0.5) * 0.2],
        },
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (key, value) => {
    const data = weatherData[key];
    if (key === 'wind') {
      if (value > data.cautionMax) return 'text-destructive';
      if (value > data.safeMax) return 'text-amber-400';
      return 'text-emerald-400';
    }
    if (key === 'waves') {
      if (value > data.cautionMax) return 'text-destructive';
      if (value > data.safeMax) return 'text-amber-400';
      return 'text-emerald-400';
    }
    if (key === 'surfaceTemp') {
      if (value < data.optimalMin || value > data.optimalMax) return 'text-amber-400';
      return 'text-emerald-400';
    }
    if (key === 'pressure') {
      if (value < data.lowThreshold) return 'text-destructive';
      if (value > data.highThreshold) return 'text-emerald-400';
      return 'text-amber-400';
    }
    return 'text-foreground';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'rising':
        return <TrendingUp className="h-3 w-3 text-amber-400" />;
      case 'falling':
        return <TrendingDown className="h-3 w-3 text-sky-400" />;
      default:
        return <Minus className="h-3 w-3 text-muted-foreground" />;
    }
  };

  const Sparkline = ({ data, color }) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const points = data.map((val, idx) => {
      const x = (idx / (data.length - 1)) * 100;
      const y = 100 - ((val - min) / range) * 100;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg width="100" height="24" className="ml-auto" data-testid="sparkline">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  };

  return (
    <Card className="col-span-12 md:col-span-5 animate-fade-in" data-testid="weather-panel">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wind className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Weather & Sea State</span>
          </div>
          <Badge variant="outline" className={isRealData ? "border-emerald-400 text-emerald-400" : "border-amber-400 text-amber-400"}>
            {isRealData ? '🌐 Live Data' : '📊 Simulated'}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">{dataSource}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(weatherData).map(([key, data]) => (
          <div 
            key={key} 
            className="flex items-center justify-between py-2 border-b border-border/30 last:border-0"
            data-testid={`weather-row-${key}`}
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground capitalize">
                  {key === 'surfaceTemp' ? 'Surface Temp' : key}
                </span>
                {getTrendIcon(data.trend)}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <span className={`text-base font-medium font-mono ${getStatusColor(key, data.numValue)}`} data-testid={`weather-value-${key}`}>
                  {data.value}
                </span>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {key === 'wind' && `Safe: <${data.safeMax} kt`}
                  {key === 'waves' && `Safe: <${data.safeMax} m`}
                  {key === 'surfaceTemp' && `Opt: ${data.optimalMin}-${data.optimalMax}°C`}
                  {key === 'pressure' && `Normal: ${data.lowThreshold}-${data.highThreshold} hPa`}
                </div>
              </div>
              <Sparkline data={data.history} color="hsl(var(--chart-2))" />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default WeatherPanel;
